import { useMemo } from 'react';
import { TApiApplicationDocument, TLocalFiles } from '~/modules/covenants/types.ts';
import {
  buildApplicationDocumentRowKey,
  buildApplicationDocumentStableKey,
} from '~/modules/covenants/utils.ts';
import {
  deriveGroupedDocumentStatus,
  TGroupedDocumentRowStatus,
} from '~/modules/covenants/CovenantsDataApplicationForm/utils.ts';

export type TGroupedDocument = {
  key: string;
  primaryDoc: TApiApplicationDocument;
  fileRows: TApiApplicationDocument[];
  hasFiles: boolean;
  aggregatedStatus: TGroupedDocumentRowStatus | null;
};

type TGroupedDocuments = TGroupedDocument[];

export const useGroupedDocuments = (
  applicationDocuments: TApiApplicationDocument[],
  localFiles: TLocalFiles
): TGroupedDocuments =>
  useMemo(() => {
    const groups: Array<{ key: string; rows: TApiApplicationDocument[] }> = [];
    const groupIndexMap = new Map<string, number>();

    applicationDocuments.forEach((doc) => {
      const groupKey = buildApplicationDocumentStableKey(doc);
      const existingGroupIndex = groupIndexMap.get(groupKey);

      if (existingGroupIndex == null) {
        groupIndexMap.set(groupKey, groups.length);
        groups.push({ key: groupKey, rows: [doc] });
        return;
      }

      groups[existingGroupIndex]?.rows.push(doc);
    });

    return groups.map((group) => {
      const primaryDoc = group.rows[0] as TApiApplicationDocument;
      const fileRows = group.rows.filter((document) => {
        const localFile = localFiles[buildApplicationDocumentRowKey(document)];

        return !!document.fileName || !!localFile?.file || !!document.isVirtualRow;
      });

      return {
        key: group.key,
        primaryDoc,
        fileRows,
        hasFiles: fileRows.length > 0,
        aggregatedStatus: deriveGroupedDocumentStatus(fileRows),
      };
    });
  }, [applicationDocuments, localFiles]);
